import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Settings,
  Share2,
  MoreHorizontal,
  Plus,
  Camera,
} from 'lucide-react';
import { Heading, Text, Stack, Container } from '@/components/ui/index.js';
import { useAuthStore } from '@/stores/authStore.js';
import { ProfileHeader } from './components/ProfileHeader/ProfileHeader.jsx';
import { StatsGrid } from './components/StatsGrid/StatsGrid.jsx';
import { SportBadge } from './components/SportBadge/SportBadge.jsx';
import { AchievementCard } from './components/AchievementCard/AchievementCard.jsx';
import { EventList } from './components/EventList/EventList.jsx';
import { ActivityTabs } from './components/ActivityTabs/ActivityTabs.jsx';
import styles from './ProfilePage.module.css';

// Mock data for the profile page
const userProfile = {
  id: 1,
  name: 'Jordan Smith',
  username: '@jordansmith',
  bio: 'Basketball enthusiast | Weekend warrior | Always looking for a pickup game 🏀',
  location: 'New York, NY',
  avatar: null,
  isOnline: true,
  stats: {
    eventsJoined: 47,
    eventsHosted: 12,
    sports: 5,
    followers: 128,
    following: 89,
  },
  sports: [
    { name: 'Basketball', skillLevel: 'advanced', icon: 'basketball' },
    { name: 'Running', skillLevel: 'intermediate', icon: 'running' },
    { name: 'Tennis', skillLevel: 'beginner', icon: 'tennis' },
    { name: 'Soccer', skillLevel: 'intermediate', icon: 'soccer' },
    { name: 'Swimming', skillLevel: 'beginner', icon: 'swimming' },
  ],
  achievements: [
    { id: 1, name: 'First Game', description: 'Joined your first event', unlocked: true, icon: 'star' },
    { id: 2, name: 'Social Butterfly', description: 'Joined 10 events', unlocked: true, icon: 'users' },
    { id: 3, name: 'Host with the Most', description: 'Hosted 5 events', unlocked: true, icon: 'crown' },
    { id: 4, name: 'MVP', description: 'Played 50 games', unlocked: false, progress: 47, total: 50, icon: 'trophy' },
    { id: 5, name: 'Early Bird', description: 'Join 5 morning events', unlocked: false, progress: 3, total: 5, icon: 'sun' },
  ],
  upcomingEvents: [
    { id: 1, title: 'Weekend Basketball Pickup', date: 'Sat, Dec 16', time: '2:00 PM', location: 'Central Park' },
    { id: 2, title: 'Morning Running Group', date: 'Sun, Dec 17', time: '7:00 AM', location: 'Riverside Trail' },
  ],
  eventHistory: [
    { id: 3, title: 'Tennis Doubles', date: 'Dec 10, 2023', location: 'City Tennis Club', rating: null },
    { id: 4, title: 'Basketball Pickup', date: 'Dec 3, 2023', location: 'Metro Sports', rating: 5 },
  ],
  highlights: [
    { id: 1, type: 'photo', url: null },
    { id: 2, type: 'photo', url: null },
    { id: 3, type: 'photo', url: null },
    { id: 4, type: 'add', url: null },
  ],
};

/**
 * ProfilePage - User profile dashboard
 * 
 * Features:
 * - Profile header with avatar glow effect
 * - Stats dashboard with animated numbers
 * - Sports and skills section
 * - Achievements with locked/unlocked states
 * - Activity tabs (Upcoming/History)
 * - Highlights section
 */
export function ProfilePage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState('upcoming');
  const [animatedStats, setAnimatedStats] = useState({
    eventsJoined: 0,
    eventsHosted: 0,
    sports: 0,
    followers: 0,
  });
  const [showShareToast, setShowShareToast] = useState(false);

  // Animate stats numbers on load
  useEffect(() => {
    const duration = 1500;
    const steps = 30;
    const interval = duration / steps;
    let step = 0;

    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      const easeOut = 1 - Math.pow(1 - progress, 3);

      setAnimatedStats({
        eventsJoined: Math.round(userProfile.stats.eventsJoined * easeOut),
        eventsHosted: Math.round(userProfile.stats.eventsHosted * easeOut),
        sports: Math.round(userProfile.stats.sports * easeOut),
        followers: Math.round(userProfile.stats.followers * easeOut),
      });

      if (step >= steps) {
        clearInterval(timer);
      }
    }, interval);

    return () => clearInterval(timer);
  }, []);

  const handleSettingsClick = () => {
    navigate('/settings');
  };

  const handleShareClick = () => {
    setShowShareToast(true);
    setTimeout(() => setShowShareToast(false), 2000);
  };

  const handleEditProfile = () => {
    // Placeholder for edit profile action
    console.log('Edit profile clicked');
  };

  const handleEventClick = (eventId) => {
    navigate(`/events/${eventId}`);
  };

  const handleCancelEvent = (eventId) => {
    // Placeholder for cancel event action
    console.log('Cancel event:', eventId);
  };

  const handleRateEvent = (eventId) => {
    // Placeholder for rate event action
    console.log('Rate event:', eventId);
  };

  return (
    <div className={styles.page}>
      {/* Top Navigation Bar */}
      <header className={styles.topNav}>
        <div className={styles.topNavContent}>
          <button
            className={styles.iconButton}
            onClick={handleSettingsClick}
            aria-label="Settings"
          >
            <Settings size={24} />
          </button>
          <Stack direction="row" gap={2}>
            <button
              className={styles.iconButton}
              onClick={handleShareClick}
              aria-label="Share profile"
            >
              <Share2 size={24} />
            </button>
            <button
              className={styles.iconButton}
              aria-label="More options"
            >
              <MoreHorizontal size={24} />
            </button>
          </Stack>
        </div>
      </header>

      {/* Share Toast */}
      {showShareToast && (
        <div className={styles.shareToast}>
          <Text size="sm" color="primary">Profile link copied!</Text>
        </div>
      )}

      <main className={styles.main}>
        <Container size="full" padding={false}>
          <Stack direction="column" gap={6}>
            {/* Profile Header */}
            <ProfileHeader
              name={userProfile.name}
              username={userProfile.username}
              bio={userProfile.bio}
              location={userProfile.location}
              avatar={userProfile.avatar}
              isOnline={userProfile.isOnline}
              onEditProfile={handleEditProfile}
            />

            {/* Stats Dashboard */}
            <section className={styles.section}>
              <StatsGrid stats={animatedStats} />
            </section>

            {/* Sports & Skills Section */}
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <Heading level={3} size="md" color="primary">My Sports</Heading>
              </div>
              <div className={styles.sportsList}>
                {userProfile.sports.map((sport, index) => (
                  <SportBadge
                    key={sport.name}
                    name={sport.name}
                    skillLevel={sport.skillLevel}
                    icon={sport.icon}
                    delay={index * 100}
                  />
                ))}
              </div>
            </section>

            {/* Achievements Section */}
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <Heading level={3} size="md" color="primary">Achievements</Heading>
                <button className={styles.viewAllLink}>
                  <Text size="sm" color="cyan">View All</Text>
                </button>
              </div>
              <div className={styles.achievementsScroll}>
                {userProfile.achievements.map((achievement, index) => (
                  <AchievementCard
                    key={achievement.id}
                    achievement={achievement}
                    delay={index * 100}
                  />
                ))}
              </div>
            </section>

            {/* Activity Section */}
            <section className={styles.section}>
              <ActivityTabs activeTab={activeTab} onTabChange={setActiveTab} />
              <div className={styles.tabContent}>
                {activeTab === 'upcoming' ? (
                  <EventList
                    events={userProfile.upcomingEvents}
                    type="upcoming"
                    onEventClick={handleEventClick}
                    onCancelEvent={handleCancelEvent}
                  />
                ) : (
                  <EventList
                    events={userProfile.eventHistory}
                    type="history"
                    onEventClick={handleEventClick}
                    onRateEvent={handleRateEvent}
                  />
                )}
              </div>
            </section>

            {/* Highlights Section */}
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <Heading level={3} size="md" color="primary">Highlights</Heading>
              </div>
              <div className={styles.highlightsGrid}>
                {userProfile.highlights.map((highlight) => (
                  <div
                    key={highlight.id}
                    className={`${styles.highlightCard} ${highlight.type === 'add' ? styles.highlightAdd : ''}`}
                  >
                    {highlight.type === 'add' ? (
                      <>
                        <Plus size={32} className={styles.highlightIcon} />
                        <Text size="xs" color="muted">Add Highlight</Text>
                      </>
                    ) : (
                      <Camera size={32} className={styles.highlightIcon} />
                    )}
                  </div>
                ))}
              </div>
            </section>
          </Stack>
        </Container>
      </main>
    </div>
  );
}

export default ProfilePage;
